import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import {
  createClinicForm,
  deleteFromById,
  getClinicForms,
  updateFromById,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { a4PrintConfig } from "../../../util/commonConfig";
import { serviceTypes } from "../../../util/dropList";
import { printFromNode, warmPrintFrame } from "../../../util/fastPrint";

const tableHeaders = [
  "name",
  "serviceType",
  "createdBy",
  "lastUpdated",
  "extraStatus",
];
const initValue = { name: "", content: "", serviceType: "", linkedServices: [] };

const mapLinkedServicesFromApi = (linkedServices, serviceList) =>
  (linkedServices ?? []).map((entry) => {
    const serviceId =
      typeof entry === "object"
        ? (entry?.serviceId ?? entry?.id ?? entry?.value)
        : entry;
    const match = (serviceList ?? []).find(
      (opt) => String(opt?.serviceId ?? opt?.value) === String(serviceId),
    );
    if (match) return match;
    const name =
      typeof entry === "object"
        ? (entry?.serviceName ?? entry?.name ?? String(serviceId))
        : String(serviceId);
    return {
      name,
      serviceName: name,
      value: serviceId,
      serviceId,
      category: "",
      subCategory: "",
    };
  });

const toLinkedServiceIds = (linkedServices) =>
  (linkedServices ?? [])
    .map((service) =>
      Number(
        typeof service === "object"
          ? (service?.serviceId ?? service?.value)
          : service,
      ),
    )
    .filter((id) => !Number.isNaN(id) && id > 0);

const delBtn = { color: AppColors.red, width: 20, height: 20 };
const edtBtn = { color: AppColors.appPrimary, width: 20, height: 20 };
const defObj = { dataSharing: {}, vets: [] };

const Forms = () => {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);
  const contentToPrint = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [createVsble, setCreateVsble] = useState(false);
  const [formValues, setFormValues] = useState(initValue);
  const [formErrs, setFormErrs] = useState({});
  const [prevModVsble, setPrevModVsble] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const { details: clinicDet, forms: clinicForms, services: clinicServices } =
    useSelector((state) => state?.clinic);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { vets: clinicVets, mandateServices } = useMemo(() => {
    if (!mandateDataRaw) return { ...defObj, mandateServices: [] };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        dataSharing: reqData?.dataSharing ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        mandateServices: Array.isArray(reqData?.services)
          ? reqData.services
          : [],
      };
    } catch {
      return { ...defObj, mandateServices: [] };
    }
  }, [mandateDataRaw]);

  const serviceList = useMemo(() => {
    const source =
      clinicServices?.data?.length > 0
        ? clinicServices.data
        : Array.isArray(clinicServices) && clinicServices.length > 0
          ? clinicServices
          : mandateServices;
    return (source ?? [])
      ?.filter((ser) => ser?.active !== "N")
      ?.map((ser) => ({
        name: ser?.serviceName ?? ser?.name ?? "",
        serviceName: ser?.serviceName ?? ser?.name ?? "",
        value: ser?.serviceId ?? ser?.serviceName ?? ser?.name,
        serviceId: ser?.serviceId,
        category: ser?.category ?? "",
        subCategory: ser?.subCategory ?? "",
      }))
      ?.filter((opt) => opt?.name && opt?.value != null && opt?.value !== "");
  }, [clinicServices, mandateServices]);

  useEffect(() => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    onApiCall();
  }, [dispatch]);

  const onApiCall = (url = "") => {
    dispatch(getClinicForms(url));
  };

  const handleDelete = (frm) => {
    setSelectedForm(frm);
    setDelModVsble(true);
  };

  const handleEdit = (frm) => {
    setFormValues({
      name: frm?.name,
      serviceType: frm?.serviceType,
      content: frm?.content,
      linkedServices: mapLinkedServicesFromApi(
        frm?.linkedServices,
        serviceList,
      ),
    });
    setSelectedForm(frm);
    setCreateVsble(true);
  };

  const getTableForms = () => {
    return clinicForms?.map((cf) => ({
      ...cf,
      serviceType: cf?.serviceType,
      createdBy:
        clinicVets?.find((vt) => vt?.doctorId === cf?.createdBy)?.name ??
        (cf?.createdBy === clinicDet?.id ? clinicDet?.name : null) ??
        "-",
      lastUpdated: moment(new Date(cf?.updatedDate)).format("DD MMM YYYY"),
      extraStatus: (
        <div className="flex-row">
          <div className="cursor" onClick={() => handleDelete(cf)}>
            <div className="flex-center">
              <DeleteOutlineIcon sx={delBtn} />
            </div>
            <Typography className="red3 fs8 txt-semi-bold text-align-center">
              Delete
            </Typography>
          </div>
          <div className="cursor ml20" onClick={() => handleEdit(cf)}>
            <div className="flex-center">
              <EditIcon sx={edtBtn} />
            </div>
            <Typography className="blue-color fs8 txt-semi-bold text-align-center">
              Edit
            </Typography>
          </div>
        </div>
      ),
    }));
  };

  const handleCreate = () => {
    setSelectedForm(null);
    setFormValues(initValue);
    setFormErrs({});
    setCreateVsble(true);
  };

  const handleChange = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSuccess = () => {
    onApiCall();
    setCreateVsble(false);
    setFormValues(initValue);
    setFormErrs({});
  };

  const getAvailableServiceTypes = () => {
    const usedTypes = new Set(
      (clinicForms ?? [])
        ?.filter((cf) => cf?.formId !== selectedForm?.formId)
        ?.map((cf) => cf?.serviceType)
        ?.filter(Boolean),
    );
    return [{ name: "Admission", value: "Admission" }, ...serviceTypes]?.filter(
      (st) => !usedTypes.has(st.value),
    );
  };

  const onSave = () => {
    if (formValues?.name?.length === 0 || !formValues?.serviceType) {
      return setFormErrs({
        name: formValues?.name?.length === 0 ? true : false,
        serviceType: !formValues?.serviceType ? true : false,
      });
    }
    const data = {
      name: formValues?.name,
      serviceType: formValues?.serviceType,
      content: formValues?.content,
      signed: "false",
      linkedServices: toLinkedServiceIds(formValues?.linkedServices),
    };
    if (selectedForm) {
      dispatch(updateFromById({ formId: selectedForm?.formId, data })).then(
        (res) => {
          if (res?.payload) {
            handleSuccess();
          }
        },
      );
    } else {
      dispatch(createClinicForm(data)).then((res) => {
        if (res?.payload) {
          handleSuccess();
        }
      });
    }
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedForm(null);
  };

  const onCofirmDelete = () => {
    if (!selectedForm) return;
    dispatch(deleteFromById(selectedForm?.formId)).then((res) => {
      if (res?.payload) {
        setDelModVsble(false);
        onApiCall();
        setSelectedForm(null);
      }
    });
  };

  useEffect(() => {
    if (!prevModVsble || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = contentToPrint.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    const node = contentToPrint.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [prevModVsble, formValues?.name, selectedForm]);

  const handlePrint = () => {
    printFromNode(contentToPrint.current, {
      title: `${formValues?.name}-${Date.now()}`,
      pageStyle: a4PrintConfig,
    });
  };

  return (
    <>
      {createVsble ? (
        <div className="scroll-70vh">
          <div className="ph10Per mt20">
            <Grid container spacing={2} className="mb20">
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <CustomTextField
                  fullWidth
                  value={formValues?.name}
                  handleChange={(e) => handleChange("name", e?.target?.value)}
                  label="Name"
                  error={formErrs?.name}
                  helperText={formErrs?.name ? "This field is required" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <CustomSelect
                  list={getAvailableServiceTypes()}
                  value={formValues?.serviceType}
                  label="Service Type *"
                  handleChange={(e) =>
                    handleChange("serviceType", e?.target?.value)
                  }
                  select
                  error={!!formErrs?.serviceType}
                  helperText={
                    formErrs?.serviceType ? "This field is required" : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <SearchableSelect
                  list={serviceList}
                  value={formValues?.linkedServices ?? []}
                  handleChange={(e) =>
                    handleChange("linkedServices", e?.target?.value ?? [])
                  }
                  multiSelectTagCheck
                  maxFilteredOptions={100}
                  searchFields={[
                    "name",
                    "serviceName",
                    "category",
                    "subCategory",
                  ]}
                  backgroundColor={AppColors.white}
                  label="Linked Services"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <div className="flex1-end">
                  <div>
                    <CustomButton
                      text="Preview"
                      smallBtn
                      onClick={() => setPrevModVsble(true)}
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text={selectedForm ? "Update" : "Save"}
                      smallBtn
                      onClick={onSave}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>

            <ReactQuill
              onChange={(e) => handleChange("content", e)}
              value={formValues?.content}
            />
            <div className="mt100">
              <div className="flex-row-ali-cen">
                {/* {formValues?.serviceType === "Form" ? ( */}
                <Typography className="fs16 font-bold black">
                  Signature of pet parent
                </Typography>
                {/* ) : null} */}
                <div className="flex1-end">
                  <Typography className="fs16 font-bold black">
                    Signature of licensed veterinarian
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex-row-ali-cen flex-center mt5">
            <div className="w30Per">
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightBlue2}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            <div className="ml20">
              <CustomButton text="Create" smallBtn onClick={handleCreate} />
            </div>
          </div>
          <CustomTable
            columns={tableHeaders}
            datas={getTableForms()}
            grey
            maxHeight="70vh"
          />
        </div>
      )}
      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete Form"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the form?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onCofirmDelete}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={prevModVsble}
        onClose={() => setPrevModVsble(false)}
        header=""
        modal
        modalWidth={90}
      >
        <div className="scroll-70vh">
          <div ref={contentToPrint} className="bg ph20">
            <div className="flex-row-ali-cen mv20">
              <div className="w20Per">
                <div className="pl20">
                  <div>
                    {clinicDet?.image ? (
                      <img src={clinicDet?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {clinicDet?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w60Per flex-center">
                <div className="font-bold fs30 blue-color">
                  {formValues?.name}
                </div>
              </div>
              <div className="w20Per flex1-end">
                <div className="pr10">
                  <img
                    src={require("../../../assets/images/png/VetInstantLogo.png")}
                    alt=""
                    className="wh50 blu-back p5"
                  />
                </div>
              </div>
            </div>
            <div className="yellow-bar" />
            <div dangerouslySetInnerHTML={{ __html: formValues?.content }} />
            <div className="mt100">
              <div className="flex-row-ali-cen">
                {/* {formValues?.serviceType === "Form" ? ( */}
                <Typography className="fs16 font-bold black">
                  Signature of pet parent
                </Typography>
                {/* ) : null} */}
                <div className="flex1-end">
                  <Typography className="fs16 font-bold black">
                    Signature of licensed veterinarian
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-center">
            <div className="mt20">
              <CustomButton text="Print" onClick={handlePrint} smallBtn />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default Forms;
